import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import cn from './cn.js';

/** Breadcrumbs: items [{ label, to }] – the last one is the current page. */
export function Breadcrumbs({ items = [], className }) {
  if (!items.length) return null;
  return (
    <nav aria-label="Breadcrumb" className={cn('flex', className)}>
      <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm">
        {items.map((item, index) => {
          const last = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`} className="flex items-center gap-x-1.5 min-w-0">
              {index > 0 && <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-300" aria-hidden="true" />}
              {last || !item.to ? (
                <span className={cn('truncate', last ? 'font-medium text-gray-900' : 'text-gray-500')} aria-current={last ? 'page' : undefined}>
                  {item.label}
                </span>
              ) : (
                <Link to={item.to} className="truncate text-gray-500 transition-colors hover:text-gray-900">
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * PageHeader: title, description, breadcrumbs, metadata row and actions.
 *
 * Detail pages get a back button automatically: it points at the closest
 * breadcrumb with a `to`. Override with `backTo="/path"` or hide it with
 * `backTo={false}`.
 */
export default function PageHeader({ title, description, actions, breadcrumbs, eyebrow, meta, className, children, size = 'lg', spacing = true, backTo, backLabel }) {
  const parent = breadcrumbs && breadcrumbs.length > 1 ? [...breadcrumbs.slice(0, -1)].reverse().find((item) => item.to) : null;
  const backTarget = backTo === false ? null : backTo || parent?.to || null;
  const backText = backLabel || (parent?.label ? `Back to ${parent.label}` : 'Back');

  return (
    <div className={cn(spacing && 'mb-6 sm:mb-8', className)}>
      {breadcrumbs && <Breadcrumbs items={breadcrumbs} className="mb-3" />}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          {backTarget && (
            <Link
              to={backTarget}
              aria-label={backText}
              title={backText}
              className={cn(
                'inline-flex shrink-0 items-center justify-center rounded-lg bg-white text-gray-500 shadow-sm ring-1 ring-inset ring-gray-200 transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                size === 'lg' ? 'mt-0.5 h-9 w-9 sm:mt-1' : 'h-8 w-8',
              )}
            >
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
          )}
          <div className="min-w-0 flex-1">
            {eyebrow && <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-indigo-600">{eyebrow}</p>}
            {title && (
              <h1 className={cn('font-semibold tracking-tight text-gray-900', size === 'lg' ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl', 'truncate')}>
                {title}
              </h1>
            )}
            {description && <p className="mt-1.5 max-w-3xl text-sm text-gray-500">{description}</p>}
            {meta && <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-500">{meta}</div>}
            {children}
          </div>
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2 lg:ml-6">{actions}</div>}
      </div>
    </div>
  );
}

/** Forces the following PageHeader.meta items (e.g. ID chips) onto their own row. */
export function MetaBreak() {
  return <span className="block h-0 basis-full" aria-hidden="true" />;
}

/** A metadata item (icon + text) for PageHeader.meta rows. */
export function MetaItem({ icon: Icon, children, className, title }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)} title={title}>
      {Icon && <Icon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />}
      {children}
    </span>
  );
}
